import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { tap, take, switchMap, catchError, map, filter } from 'rxjs/operators';

import * as fromStore from '../store';
import { combineLatest, Observable, of } from 'rxjs';
import { SetTitle, SetTags, SetCanonical, SetStructuredData } from '../../store';
import { StringUtility, UrlRewritePipe } from '@teamfoster/sdk/text-utility';
import { FromDictionaryPipe, getDictionaryLoaded, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { DatePipe } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class StoryDetailMetaGuard {
  private dict = new FromDictionaryPipe(this.lang);
  private url = new UrlRewritePipe();

  constructor(private store: Store<fromStore.StoryState>, private lang: LanguageService) {}

  canActivate(): Observable<boolean> {
    return this.checkStore().pipe(
      switchMap(() => of(true)),
      catchError(() => of(false))
    );
  }

  checkStore(): Observable<boolean> {
    return combineLatest([this.store.select(fromStore.getSelectedStory), this.store.select(getDictionaryLoaded)]).pipe(
      filter(([page, dictLoaded]) => page?.id > 0 && dictLoaded && !!this.dict.transform('meta-titel-suffix', false)),
      tap(([page]) => {
        this.store.dispatch(
          SetTitle({
            title: `${StringUtility.Truncate(StringUtility.StripHTML(page.title), 157, '...')} - ${this.dict.transform(
              'meta-titel-suffix'
            )}`,
          })
        );
        this.store.dispatch(SetCanonical({ url: page.canonical }));
        this.store.dispatch(
          SetTags({
            properties: {
              'description': StringUtility.Truncate(StringUtility.StripHTML(page.intro || page.text), 157, '...'),
              'og:image': `https:${this.url.transform(page.image.url || page?.image?.url || '')}`,
              'og:title': `${StringUtility.Truncate(StringUtility.StripHTML(page.title), 157, '...')} - ${this.dict.transform(
                'meta-titel-suffix'
              )}`,
              'og:description': StringUtility.Truncate(StringUtility.StripHTML(page.intro || page.text), 157, '...'),
              'twitter:card': 'summary_large_image',
              'twitter:image': `https:${this.url.transform(page.image.url || page?.image?.url || '')}`,
              'twitter:title': `${StringUtility.Truncate(StringUtility.StripHTML(page.title), 157, '...')} - ${this.dict.transform(
                'meta-titel-suffix'
              )}`,
              'twitter:description': StringUtility.Truncate(StringUtility.StripHTML(page.intro || page.text), 157, '...'),
            },
          })
        );

        var structuredData: any = {
          '@context': 'http://schema.org',
          '@type': 'Article',
          'mainEntitiyOfPage': {
            '@type': 'WebPage',
            '@id': page.canonical,
          },
          'headline': page.title,
          'description': StringUtility.Truncate(StringUtility.StripHTML(page.intro || page.text), 157, '...'),
          'image': `https:${page.image.url || page?.image?.url}`,
          'author': {
            '@type': page.author ? 'Person' : 'Organization',
            'name': page.author?.name || 'Onze IJssel',
          },
          'publisher': {
            '@type': 'Organization',
            'name': 'Onze IJssel',
            'logo': {
              '@type': 'ImageObject',
              'url': `https://www.onzeijssel.nl/assets/gfx/onze-ijssel-logo.svg`,
            },
          },
          'datePublished': page.createdOn,
        };

        if (page.locations && page.locations.length > 0) {
          structuredData = {
            ...structuredData,
            location: {
              '@type': 'Place',
              'name': page.locations[0].label || this.dict.transform('gehele-ijssel', false) || 'De IJssel',
              'address': {
                '@type': 'PostalAddress',
                'name': page.locations[0].label || this.dict.transform('gehele-ijssel', false) || 'De IJssel',
              },
            },
          };
        } else {
          structuredData = {
            ...structuredData,
            location: {
              '@type': 'Place',
              'name': this.dict.transform('gehele-ijssel', false) || 'De IJssel',
            },
          };
        }

        this.store.dispatch(
          SetStructuredData({
            data: structuredData,
          })
        );
      }),
      map(([page, dictLoaded]) => page?.id > 0 && dictLoaded && !!this.dict.transform('meta-titel-suffix', false))
    );
  }
}
